<template>
  <div class="scrollable-widget" v-if="isLoading || getGroupedEvents.length > 0">

    <div class="custom-tooltip"
         :class="'tooltip-' + tooltipPlacement"
         :style="tooltipStyle" v-html="tooltipContent"></div>

    <!-- Skeleton -->
    <div v-if="isLoading && getGroupedEvents.length === 0" class="widget-container">
      <div class="widget" v-for="n in 8" :key="'skel_'+n" style="opacity:0.35; pointer-events:none">
        <div class="skel-blob w-30"></div>
        <div class="teams">
          <div class="team"><div class="skel-circle"></div><div class="skel-blob w-60"></div></div>
          <div class="team"><div class="skel-circle"></div><div class="skel-blob w-50"></div></div>
        </div>
        <div class="market-section">
          <div class="skel-blob w-40"></div>
          <div class="outcomes">
            <div class="skel-box" v-for="i in 3" :key="i"></div>
          </div>
        </div>
      </div>
    </div>

    <template v-else-if="getGroupedEvents.length > 0">
      <div class="widget-label">
        <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" style="fill: currentcolor; color: rgb(33, 168, 247); width: 32px; height: 32px;">
          <path d="M12.2726 1L12.9248 3.07791L14.9999 3.73008L12.7272 6.00281L11.3547 5.7283L8.58309 8.50006C8.284 8.79913 7.79908 8.79913 7.5 8.50006C7.20091 8.20099 7.20091 7.7161 7.5 7.41703L10.2735 4.64336L9.99992 3.27554L12.2726 1Z"></path>
          <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C8.56669 2 9.11503 2.07856 9.63473 2.22539L8.91405 2.94611L9.05484 3.62192L9.05394 3.6241C8.71587 3.54297 8.36295 3.5 8 3.5C5.51472 3.5 3.5 5.51472 3.5 8C3.5 10.4853 5.51472 12.5 8 12.5C10.4853 12.5 12.5 10.4853 12.5 8C12.5 7.63934 12.4576 7.28859 12.3774 6.95247L13.0606 7.09264L13.7763 6.37144C13.9221 6.88931 14 7.43555 14 8Z"></path>
          <path d="M8 5C8.15907 5 8.31524 5.01238 8.46761 5.03623L6.86728 6.61504C6.48053 6.94101 6.23483 7.42902 6.23483 7.97442C6.23483 8.95585 7.03044 9.75145 8.01186 9.75145C8.52346 9.75145 8.98456 9.53526 9.3088 9.18925L10.964 7.53402C10.9877 7.68588 11 7.8415 11 8C11 9.65685 9.65685 11 8 11C6.34315 11 5 9.65685 5 8C5 6.34315 6.34315 5 8 5Z"></path>
        </svg>


        <span>{{ $t('featured_events') || 'Featured Events' }}</span>
      </div>

      <div class="scroll-wrapper">
        <button v-if="!isSmallScreen" class="scroll-side-btn left" :class="{ active: showLeftArrow }" @click="scrollCards(-1)">‹</button>

        <div class="scroll-track">
          <div class="fade-edge left" v-show="showLeftArrow"></div>
          <div class="fade-edge right" v-show="showRightArrow"></div>

          <div class="widget-container" ref="widgetContainer" @scroll.passive="updateArrows">
            <div
              class="widget"
              v-for="event in getGroupedEvents"
              :key="event.id"
              @click="getGameConsole !== 'terminal' && goToDetail(event)"
              :style="styleMatchHero(event.sportId)"
            >
              <img
                class="widget-tournament-bg"
                :src="`${tournamentPath}${event.tournamentId}.webp`"
                alt=""
                aria-hidden="true"
                @error="e => onTournamentBgError(e, event)"
              />

              <div class="meta-row">
                <span class="match-date">{{ formatDate(event.scheduled) }}</span>
                <div class="tournament-info">
                  <img class="img-flag-filter" :src="categoryPath + event.categoryId + '.svg'" :alt="event.categoryName" @error="handleImgError">
                  <span class="tournament-name">{{ event.tournamentName }}</span>
                </div>
              </div>

              <div class="teams">
                <div class="team">
                  <LogoCompetitors :team-id="event.home_id" height="18px" width="18px"/>
                  <span class="team-name">{{ event.home }}</span>
                </div>
                <div class="team">
                  <LogoCompetitors :team-id="event.away_id" height="18px" width="18px"/>
                  <span class="team-name">{{ event.away }}</span>
                </div>
              </div>

              <div v-if="event.odds && event.odds[0]" class="market-section">
                <div class="market-header" @click.stop>
                  <span class="market-name">{{ event.odds[0].marketName }}</span>
                  <div v-if="getEnableEarlyPayout && event.earlyPayout && event.earlyPayoutMargin"
                       class="tooltip-wrapper"
                       @click.stop="isSmallScreen ? toggleMobileTooltip($event, event.id, $t('early_payout_tooltip', {numberPoint: event.earlyPayoutMargin})) : null"
                       @mouseenter="!isSmallScreen ? showTooltip($event, event.id, $t('early_payout_tooltip', {numberPoint: event.earlyPayoutMargin})) : null"
                       @mouseleave="!isSmallScreen ? hideTooltip() : null">
                    <FeatureBadge badge-key="earlyPayout" />
                  </div>
                </div>
                <div class="outcomes-slider">
                  <div class="outcomes">
                    <div class="outcome-box" v-for="odd in (event.odds[0].outcome || []).slice(0, 3)" :key="odd.unique_id">
                      <div v-if="isValidOdd(odd)"
                           class="value clickable"
                           :class="{ odd_selected: getOddsSources.includes(odd.unique_id) }"
                           @click.stop="putCart(event.odds[0], odd, event)">
                        <span class="outcome-name">{{ getOutcomeName(event.competitors, odd, odd.unique_id) }}</span>
                        <span class="outcome-value">{{ odd.outcome_odd_value | odds_type_filter }}<OddBoostBadge :odd="odd" size="sm" :show-percent="false"/></span>
                      </div>
                      <div v-else class="value locked" @click.stop><v-icon dense small>mdi-lock</v-icon></div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <button v-if="!isSmallScreen" class="scroll-side-btn right" :class="{ active: showRightArrow }" @click="scrollCards(1)">›</button>
      </div>
    </template>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { isMobileOnly, isTablet } from 'mobile-device-detect';
import LogoCompetitors from '@/components/center_widgets/LogoCompetitors.vue';
import OddBoostBadge from '@/components/generic/OddBoostBadge.vue';
import FeatureBadge from '@/components/badges/FeatureBadge.vue';
import { MARKET_NOT_NAME_IN_OUTCOME } from '../../../constants/marketConstants';

export default {
  name: 'EventBestPlayedWidgetScrollable',
  components: { LogoCompetitors, OddBoostBadge, FeatureBadge },
  data() {
    return {
      currentSportId: sessionStorage.getItem('bestPlayed_sportId') || null,
      now: new Date().getTime(),
      timerInterval: null,
      categoryPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/categories/round_flags/',
      tournamentPath: process.env.VUE_APP_URL_CLOUDFRONT + '/svg/tournaments/',
      isSmallScreen: typeof window !== 'undefined' && window.innerWidth <= 600,
      tooltipContent: '',
      tooltipStyle: {},
      tooltipPlacement: 'above',
      activeTooltipId: null,
      activeMobileTooltipId: null,
      showLeftArrow: false,
      showRightArrow: false,
    };
  },
  computed: {
    ...mapGetters({
      getRawEvents:        'bestPlayed/getBestPlayedEvents',
      getBestPlayedSports: 'bestPlayed/getBestPlayedSports',
      isLoading:           'bestPlayed/isLoadingBestPlayed',
      getOddsSources:      'cart/getOddsSources',
      getLanguage:         'settings/getLanguage',
      getEnableEarlyPayout: 'settings/getEnableEarlyPayout',
      getGameConsole:      'settings/getGameConsole',
      getCompetitorsInOutcome: 'settings/getCompetitorsInOutcome',
    }),
    hasVisibleEvents() {
      return this.getGroupedEvents.length > 0;
    },
    getFormattedEvents() {
      if (!this.getRawEvents || this.getRawEvents.length === 0) return [];
      return this.getRawEvents.map(sportObj => {
        const map = {};
        let hasEvents = false;
        if (sportObj.events) {
          Object.keys(sportObj.events)
            .sort((a, b) => new Date(a) - new Date(b))
            .forEach(dateKey => {
              const group = sportObj.events[dateKey];
              const valid = (group.events || [])
                .filter(evt => !evt.scheduled || new Date(evt.scheduled).getTime() > this.now)
                .sort((a, b) => new Date(a.scheduled) - new Date(b.scheduled));
              if (valid.length > 0) {
                map[dateKey] = { ...group, events: valid };
                hasEvents = true;
              }
            });
        }
        return hasEvents ? { ...sportObj, events: map } : null;
      }).filter(Boolean);
    },
    getGroupedEvents() {
      if (!this.getFormattedEvents || this.getFormattedEvents.length === 0) return [];
      return Object.values(this.getFormattedEvents[0].events).flatMap(item => item.events || []);
    },
  },
  watch: {
    getBestPlayedSports: {
      handler(newSports) {
        if (newSports && newSports.length > 0) {
          const valid = newSports.find(s => s.sportId == this.currentSportId);
          if (!this.currentSportId || !valid) {
            this.currentSportId = newSports[0].sportId;
            sessionStorage.setItem('bestPlayed_sportId', this.currentSportId);
          }
        }
      },
      immediate: true,
      deep: true,
    },
    hasVisibleEvents(val) {
      if (!val && !this.isLoading) this.loadData(1, false);
    },
    getGroupedEvents() {
      this.$nextTick(this.updateArrows);
    },
  },
  methods: {
    ...mapActions({
      fetchEventBestPlayedV4: 'bestPlayed/fetchEventBestPlayedV4',
      addOddToCartV3:         'cart/addOddToCartV3',
      fetchEventDetailV3:     'detailPrematch/fetchEventDetailV3',
      setLoadingPreview:      'odds/setLoadingPreview',
    }),
    styleMatchHero(sportId) {
      return {
        backgroundImage: `url(${this.getSportBg(sportId)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    },
    getSportBg(sportId) {
      return `${process.env.VUE_APP_URL_CLOUDFRONT}/svg/sports-bg-detail/${sportId}.webp`;
    },
    onTournamentBgError(e, event) {
      if (!e.target.dataset.fallback) {
        e.target.dataset.fallback = '1';
        e.target.src = `${this.categoryPath}${event.categoryId}.svg`;
      } else {
        e.target.style.display = 'none';
      }
    },
    getOutcomeName(c, o, unique_id) {
      if (MARKET_NOT_NAME_IN_OUTCOME.includes(unique_id)) {
        return o.outcome_alias || o.outcome_name;
      }
      const outcome = o.outcome_name?.toLowerCase();
      if (this.getCompetitorsInOutcome) {
        if (outcome === 'local') return c?.[0]?.name || o.outcome_alias || o.outcome_name;
        if (outcome === 'visitante') return c?.[1]?.name || o.outcome_alias || o.outcome_name;
        if ((o.outcome_name === '1' || o.outcome_name === '2') && unique_id != '128') {
          for (let i = 0; i < c.length; i++) {
            if (c[i].position === o.outcome_name) return c[i].name;
          }
        }
      }
      return o.outcome_alias || o.outcome_name;
    },
    handleImgError(e) { e.target.style.display = 'none'; },
    loadData(page = 1, force = false) {
      this.fetchEventBestPlayedV4({
        sportId: this.currentSportId,
        page,
        mobilePlatform: isMobileOnly || isTablet,
        language_user: this.getLanguage,
        force,
      });
    },
    isValidOdd(odd) {
      return odd && parseFloat(odd.outcome_odd_value) > 1 && odd.active;
    },
    async putCart(oddMarket, odd, event) {
      if (!this.isValidOdd(odd)) return;
      odd.type = 'prematch';
      await this.addOddToCartV3({
        oddMarket, odd, event,
        sport:       { id: event.sportId },
        category:    { id: event.categoryId },
        tournament:  { id: event.tournamentId, name: event.tournamentName },
      });
    },
    goToDetail(event) {
      if (window.innerWidth < 1200 || window.forceMobileView === 'true') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.setLoadingPreview({ status: true });
      this.fetchEventDetailV3({
        source:          event.id,
        group:           null,
        sport:           event.sportId,
        category:        event.categoryId,
        tournament:      event.tournamentId,
        status:          event.sport_event_status?._attributes?.status || 'not_started',
        startEventTime:  event.scheduled,
        sportName:      event.sportName,
        categoryName:   event.categoryName,
        tournamentName: event.tournamentName,
        competitorHome:  event.home,
        competitorAway:  event.away,
        competitorHomeId: event.home_id,
        competitorAwayId: event.away_id,
      }).then(() => this.setLoadingPreview({ status: false }));
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    },
    positionTooltip(rect) {
      this.$nextTick(() => {
        const tooltipEl = this.$el.querySelector('.custom-tooltip');
        if (!tooltipEl) return;
        const tipW = tooltipEl.offsetWidth;
        const tipH = tooltipEl.offsetHeight;
        const vw = window.innerWidth;
        const gap = 8;
        let top, transformY;
        if (rect.top - tipH - gap >= 0) {
          top = rect.top - gap;
          transformY = '-100%';
          this.tooltipPlacement = 'above';
        } else {
          top = rect.bottom + gap;
          transformY = '0%';
          this.tooltipPlacement = 'below';
        }
        const rawLeft = rect.left + rect.width / 2;
        let left, transformX;
        if (rawLeft - tipW / 2 < gap) { left = gap; transformX = '0%'; }
        else if (rawLeft + tipW / 2 > vw - gap) { left = vw - gap - tipW; transformX = '0%'; }
        else { left = rawLeft; transformX = '-50%'; }
        this.tooltipStyle = {
          top: top + 'px', left: left + 'px',
          transform: `translateX(${transformX}) translateY(${transformY})`,
          opacity: '1',
        };
      });
    },
    showTooltip(nativeEvent, eventId, content) {
      const rect = nativeEvent.currentTarget.getBoundingClientRect();
      this.tooltipContent = content;
      this.tooltipStyle = { top: '-9999px', left: '-9999px' };
      this.activeTooltipId = eventId;
      this.positionTooltip(rect);
    },
    hideTooltip() {
      this.activeTooltipId = null;
      this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
    },
    toggleMobileTooltip(nativeEvent, eventId, content) {
      if (this.activeMobileTooltipId === eventId) {
        this.closeMobileTooltip();
      } else {
        const rect = nativeEvent.currentTarget.getBoundingClientRect();
        this.tooltipContent = content;
        this.tooltipStyle = { top: '-9999px', left: '-9999px' };
        this.activeMobileTooltipId = eventId;
        this.positionTooltip(rect);
        if (!this._mobileTooltipOutside) {
          this._mobileTooltipOutside = (e) => {
            if (!e.target.closest('.tooltip-wrapper')) this.closeMobileTooltip();
          };
          document.addEventListener('click', this._mobileTooltipOutside);
        }
      }
    },
    closeMobileTooltip() {
      this.activeMobileTooltipId = null;
      this.tooltipStyle = { ...this.tooltipStyle, opacity: '0' };
      if (this._mobileTooltipOutside) {
        document.removeEventListener('click', this._mobileTooltipOutside);
        this._mobileTooltipOutside = null;
      }
    },
    updateArrows() {
      const el = this.$refs.widgetContainer;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      this.showLeftArrow = scrollLeft > 4;
      this.showRightArrow = scrollLeft + clientWidth < scrollWidth - 4;
    },
    scrollCards(direction) {
      const el = this.$refs.widgetContainer;
      if (!el) return;
      el.scrollBy({ left: direction * 260, behavior: 'smooth' });
      setTimeout(this.updateArrows, 300);
    },
    onResize() {
      this.isSmallScreen = window.innerWidth <= 600;
      this.$nextTick(this.updateArrows);
    },
  },
  mounted() {
    this.loadData();
    window.addEventListener('resize', this.onResize);
    this.timerInterval = setInterval(() => {
      this.now = new Date().getTime();
      if (!this.hasVisibleEvents && !this.isLoading) this.loadData(1, false);
    }, 30000);
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    window.removeEventListener('resize', this.onResize);
    if (this._mobileTooltipOutside) document.removeEventListener('click', this._mobileTooltipOutside);
  },
};
</script>

<style scoped>
.widget-label {
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-7xl);
  font-weight: 700;
  color: var(--text-platform);
  margin-bottom: 2px;
}

.scroll-wrapper {
  position: relative;
}

.scroll-side-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 28px;
  height: 28px;
  background: var(--hsla-4);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 50%;
  color: var(--text-platform, #fff);
  font-size: var(--font-size-8xl);
  line-height: 1;
  cursor: pointer;
  user-select: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}

.scroll-side-btn.left  { left:  4px; }
.scroll-side-btn.right { right: 4px; }

.scroll-wrapper:hover .scroll-side-btn.active {
  opacity: 1;
  pointer-events: auto;
}

.scroll-side-btn.active:hover {
  opacity: 1;
  filter: brightness(1.2);
}

.scroll-track {
  position: relative;
  min-width: 0;
  overflow: hidden;
}

.fade-edge {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 20px;
  z-index: 2;
  pointer-events: none;
  transition: opacity 0.2s;
}

.fade-edge.left {
  left: 0;
  background: linear-gradient(to right, var(--bg-body, #14161d) 10%, transparent);
}

.fade-edge.right {
  right: 0;
  background: linear-gradient(to left, var(--bg-body, #14161d) 10%, transparent);
}

.widget-container {
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding: 12px 0;
  scrollbar-width: none;
}

.widget-container::-webkit-scrollbar { display: none; }

.widget {
  flex: 0 0 auto;
  width: 240px;
  border-radius: 10px;
  padding: 10px;
  background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.02));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: var(--shadow-card);
  position: relative;
  overflow: hidden;
}

@media (hover: hover) {
  .widget:hover { transform: translateY(-2px); }
  .widget:active { opacity: 0.75; transform: none; }
}

.widget::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background: var(--bg-before-widget-featured-events-scroll);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
}

.widget::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  z-index: 1;
  background: linear-gradient(to bottom, transparent 0%, var(--bg-panel) 100%);
  pointer-events: none;
}

.widget > * {
  position: relative;
  z-index: 2;
}

.widget-tournament-bg {
  position: absolute !important;
  right: -16px;
  top: 50%;
  transform: translateY(-50%);
  width: 100px;
  height: 100px;
  object-fit: contain;
  opacity: 0.08;
  pointer-events: none;
  z-index: 1 !important;
  -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%);
  mask-image: linear-gradient(to right, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%);
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
}

.match-date {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-op-50-platform);
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.tournament-info {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  overflow: hidden;
}

.img-flag-filter {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  object-fit: contain;
  opacity: 0.8;
}

.tournament-name {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.teams { display: flex; flex-direction: column; gap: 5px; }
.team { display: flex; align-items: center; gap: 6px; min-width: 0; }
.team-name {
  font-size: var(--font-size-2xl);
  font-weight: 600;
  color: var(--text-platform);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.teams .team img { width: 18px; height: 18px; }

.market-section {
  margin-top: auto;
  border-top: 1px solid rgba(255,255,255,0.1);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.market-header { display: flex; align-items: center; justify-content: space-between; gap: 4px; height: 28px; }
.market-name {
  font-size: var(--font-size-2xl);
  line-height: 14px;
  color: var(--text-op-50-platform);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.outcomes-slider { overflow: hidden; }

.outcomes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.outcomes .outcome-box { width: 100%; min-width: 0; }
.outcomes .outcome-box .value { width: 100%; min-width: 0; }
.outcomes .outcome-box .outcome-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.tooltip-wrapper { display: inline-flex; align-items: center; cursor: pointer; flex-shrink: 0; }
.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: var(--bg-card, #1e2130);
  border: 1px solid var(--border-color, rgba(255,255,255,0.15));
  color: var(--text-platform, #fff);
  font-size: var(--font-size-2xl);
  padding: 6px 10px;
  border-radius: 6px;
  max-width: 220px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  white-space: normal;
  line-height: 1.4;
}
.custom-tooltip.tooltip-above { transform-origin: bottom center; }
.custom-tooltip.tooltip-below { transform-origin: top center; }

.skel-blob { height: 8px; border-radius: 4px; background: rgba(255,255,255,0.12); animation: skel-pulse 1.5s ease-in-out infinite; }
.skel-blob.w-30 { width: 30%; }
.skel-blob.w-40 { width: 40%; }
.skel-blob.w-50 { width: 50%; }
.skel-blob.w-60 { width: 60%; }
.skel-circle { width: 18px; height: 18px; border-radius: 50%; background: rgba(255,255,255,0.12); flex-shrink: 0; animation: skel-pulse 1.5s ease-in-out infinite; }
.skel-box { height: 36px; border-radius: 6px; background: rgba(255,255,255,0.1); animation: skel-pulse 1.5s ease-in-out infinite; }
@keyframes skel-pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 0.2; } }
</style>
